<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {computed, onMounted, reactive} from "vue";
import TextInput from "@/Components/TextInput.vue";
import {useErrorStore} from "@/Stores/errorStore";
import { useUserStore } from "@/Stores/userStore";
import {useToast} from "vue-toastification";
import { bookletApi } from "@/Api/booklet-api";

const toast = useToast();
const emits = defineEmits(['close', 'created']);
const errorStore = useErrorStore();
const userStore = useUserStore();

const props = defineProps({
    title: {
        type: String,
        default: "Create Collection"
    },
    show: {
        type: Boolean,
        default: false,
    },
    category: {
        type: String,
        default: null,
    }
});

const data = reactive({
    request: {
        title: null,
        description: null,
    },
    isLoading: false,
})

const isFormValid = computed(() => {
    return data.request.title && data.request.title.trim().length > 0;
});

function createBookletRequest() {
    data.isLoading = true;
    const request = {
        ...data.request,
        category: props.category,
    };
    bookletApi.postCreateBooklet(request).then(response => {
        data.isLoading = false;
        toast.success('Successfully created a new collection.');
        data.request.title = null;
        data.request.description = null;
        emits('close');
        emits('created', response.data.booklet?.id);
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

onMounted(() => {
    errorStore.clearErrors()
})

function createBooklet() {
    createBookletRequest();
}

function closeModal() {
    emits('close');
}

</script>

<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="closeModal">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-900/80 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="w-full max-w-md rounded-lg shadow-xl overflow-hidden">
                            <!-- Header -->
                            <div class="flex items-center justify-between px-6 py-4 bg-gray-800 border-b border-gray-700">
                                <div class="flex items-center gap-3">
                                    <FontAwesomeIcon :icon="['fad', 'books']" class="text-blue-400 text-xl" />
                                    <h2 class="text-xl font-bold text-white">{{ props.title }}</h2>
                                </div>
                                <button
                                    @click="closeModal"
                                    class="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <FontAwesomeIcon :icon="['far', 'xmark']" class="w-5 h-5" />
                                </button>
                            </div>

                            <!-- Body -->
                            <div class="px-6 py-5 bg-gray-800 text-left">
                                <div class="space-y-5">
                                    <div>
                                        <InputLabel id="title" for="title" value="Title" class="text-gray-300 mb-2"/>
                                        <TextInput
                                            id="title"
                                            class="block w-full bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                                            v-model="data.request.title"
                                            placeholder="Enter collection title..."
                                        />
                                    </div>
                                    <div>
                                        <InputLabel id="description" for="description" value="Description (optional)" class="text-gray-300 mb-2"/>
                                        <textarea
                                            id="description"
                                            class="block w-full h-28 px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 rounded-lg resize-none"
                                            v-model="data.request.description"
                                            placeholder="Describe your collection..."
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Footer -->
                            <div class="flex items-center justify-end px-6 pb-6 bg-gray-800">
                                <button
                                    @click="createBooklet"
                                    :disabled="!isFormValid || data.isLoading"
                                    :class="[
                                        'px-5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2',
                                        isFormValid && !data.isLoading
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                    ]"
                                >
                                    <FontAwesomeIcon v-if="data.isLoading" :icon="['fas', 'spinner']" spin />
                                    Create Collection
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
